'use client';

import { useEffect, useRef, useState } from 'react';
import { HlsVideo, type HlsStreamState } from './HlsVideo';
import {
  estimateWebrtcLatency,
  webrtcStatsSample,
  type VideoLatencyEstimate,
  type WebrtcStatsSample,
} from '@/lib/video-latency';
import { whepUrlFromStreamUrl } from '@/lib/stream-url';

/** How often the WebRTC stats are re-read, ms. Matches `HlsVideo`'s cadence so
 *  the badge updates at the same rate whichever transport won. */
const WHEP_LATENCY_SAMPLE_MS = 1000;

/** How often an HLS-fallback mount re-offers WHEP, hoping to upgrade. The
 *  aircraft's publish appears within seconds of it streaming, so 8 s bounds the
 *  discovery delay; the cost per tick is one small POST and a connection that
 *  never does ICE. */
const WHEP_RETRY_MS = 8000;

/**
 * How long a negotiated connection may deliver no DECODED frame before this
 * player gives up on it and lets HLS paint.
 *
 * Timed from the moment the answer is applied, not from the offer: a slow proxy
 * hop is a different failure from a silent connection, and only the second one
 * says anything about the network.
 */
const WHEP_MEDIA_DEADLINE_MS = 6000;

/** How long the WHEP POST itself may take before it is abandoned. Nothing may
 *  hang for ever, and an unanswered offer is the case the probe re-asks. */
const WHEP_ANSWER_TIMEOUT_MS = 6000;

/**
 * How long to let ICE gathering finish before the offer is sent anyway.
 *
 * We do not trickle: there is no `onicecandidate` handler and no PATCH back to
 * the WHEP resource (MediaMTX's own reader page does both). So whatever is in
 * the SDP at POST time is every candidate the server will ever have of ours -
 * and an offer sent the instant `setLocalDescription` resolves contains NONE.
 * The server is then left to discover us peer-reflexively from our own binding
 * requests, which works often enough to look fine and fails often enough to
 * matter: 5 of ~18 sessions on prod 2026-09-02 closed `deadline exceeded while
 * waiting connection`.
 *
 * Bounded, because gathering can hang behind a firewall that swallows STUN: a
 * partial candidate set beats no offer, and the media deadline still covers
 * the case where even that does not connect.
 */
const WHEP_ICE_GATHER_MS = 1500;

/**
 * How long a picture may stay frozen before the player stops calling it live.
 *
 * A remote track goes muted when media stops arriving. Brief mutes are ordinary
 * on a roaming aircraft and recover on their own, so reacting to the first one
 * would flap - the same reason `disconnected` is not treated as fatal below.
 * What must NOT survive is the other end of it: the publisher drops, ICE stays
 * happily `connected`, and the <video> holds its last decoded frame under a
 * live aircraft's callsign while the player still reports `playing`. Two
 * seconds is long enough to ride out a blip and short enough that nobody reads
 * a still photograph as a live picture.
 */
const WHEP_MUTE_GRACE_MS = 2000;

/**
 * How many negotiations may end without a picture before this player stops
 * re-offering the mount and settles on HLS.
 *
 * A 201 proves the route and the publisher; it proves nothing about ICE, and
 * nothing at all about whether the browser can DECODE what arrives. On a
 * network where WebRTC cannot work, every probe would win its 201, flip back,
 * and blank the picture for a whole deadline before falling back again. Two
 * strikes buys a genuinely slow first negotiation the retry it deserves, and
 * costs a hopeless network ~12 s before it settles on the picture HLS can give.
 */
const MAX_MEDIALESS_WHEP_ATTEMPTS = 2;

/**
 * How long a mount that has spent its strikes must wait before it may offer
 * again, and the ceiling that wait grows to.
 *
 * MAX_MEDIALESS_WHEP_ATTEMPTS was written for a network that CANNOT carry
 * WebRTC, where re-offering is pure noise - and as a PERMANENT stop it was
 * wrong for the failure this platform actually has. A mount earns its strikes
 * during a transient uplink stall: MediaMTX answers 201 with a publisher that
 * has stopped sending, no frame decodes, the deadline fires. Two of those - ever
 * - and the tab was on HLS until someone reloaded it, because the only thing
 * that clears a strike is a decoded WHEP frame and nothing was left asking for
 * one.
 *
 * Measured on prod 2026-09-07: the site kiosk pulled 400+ HLS requests a minute
 * for hours with ZERO WHEP offers - not even the retry probe - while a freshly
 * loaded page on the same network negotiated sub-second video first try. The
 * operator was watching 4-9 s video under a green health dot; the sub-second
 * path was one offer away and nothing would ever make it.
 *
 * So the ceiling becomes a BACKOFF rather than a stop. Each strike past it
 * doubles the wait, capped, which keeps the "do not flap every 8 s" protection
 * the ceiling exists for - a hopeless network pays one 6 s deadline per cap
 * interval, under 1% of the time - while guaranteeing that a mount which
 * becomes healthy is found again without a page reload.
 */
const WHEP_STRIKE_COOLDOWN_MS = 60_000;
const WHEP_STRIKE_COOLDOWN_CAP_MS = 900_000;

interface MediaLessStrike {
  count: number;
  /** When the most recent strike landed, epoch ms. The backoff runs from here. */
  at: number;
}

/**
 * Negotiations that ended without a picture, per MOUNT - module scope on
 * purpose, not a ref.
 *
 * "This network cannot carry WebRTC to this mount" is a fact about the network
 * and the mount, not about one React component instance. Held in a ref it died
 * on every remount, and FEED is conditionally rendered: an operator flipping to
 * LIVE and back during a descent re-paid the whole discovery cost - two blanked
 * pictures - every single time. Keyed by URL so a different aircraft never
 * inherits a verdict, and reset the moment a frame decodes.
 *
 * Module scope is also what made the old permanent ceiling so expensive: it
 * outlives every remount and every route change, so the verdict could only be
 * revoked by reloading the page. It carries a timestamp now so the verdict can
 * expire on its own - see WHEP_STRIKE_COOLDOWN_MS.
 */
const mediaLessStrikes = new Map<string, MediaLessStrike>();

/**
 * May this mount be offered WHEP again yet?
 *
 * Under the ceiling, always - a slow first negotiation deserves its retry. Over
 * it, only once the doubling backoff has elapsed. `now` is a parameter so the
 * decision is a pure function of the strike record and the clock.
 */
function whepProbeAllowed(url: string, now: number): boolean {
  const strike = mediaLessStrikes.get(url);
  if (!strike || strike.count < MAX_MEDIALESS_WHEP_ATTEMPTS) return true;
  const over = strike.count - MAX_MEDIALESS_WHEP_ATTEMPTS;
  const wait = Math.min(
    WHEP_STRIKE_COOLDOWN_MS * 2 ** over,
    WHEP_STRIKE_COOLDOWN_CAP_MS,
  );
  const elapsed = now - strike.at;
  // A wall clock that steps BACKWARDS - an NTP correction, a laptop resuming
  // from sleep - would otherwise park the mount on HLS for the size of the
  // jump, which is the exact failure this whole change exists to remove. An
  // unreadable elapsed time counts as "long enough": the cost of asking too
  // early is one offer, and the cost of not asking is the sortie.
  return elapsed < 0 || elapsed >= wait;
}

interface LiveVideoProps {
  /** HLS (.m3u8) or direct .mp4 URL, as handed out by the stream registry. */
  url: string;
  label: string;
  className?: string;
  controls?: boolean;
  /**
   * Reports the HLS player's state, so a surface can stop drawing over a
   * picture that has given up (a latency badge stamped across "stream offline"
   * is worse than no badge).
   *
   * Every WHEP failure falls back to HLS, so the player that ends up owning a
   * dead picture is always the HLS one -- but the WHEP path reports `playing`
   * on its first track, because a consumer that remembers WHICH stream failed
   * would otherwise keep a good picture suppressed for ever if that same stream
   * later came back over WebRTC. Silence would have been safe only while
   * `webrtc: no` ships.
   */
  onStreamState?: (state: HlsStreamState) => void;
  /**
   * The player's own estimate of how far behind live it is, reported about once
   * a second on WHICHEVER transport won.
   *
   * The two paths measure the same delay from different instruments, because
   * they have to: `hls.js` computes the distance from the playhead to the live
   * edge, while a MediaStream has no playhead and no seekable range at all —
   * there `getStats()` gives the delay as its parts (jitter buffer, decode,
   * half the round trip). Both are player-side and both under-state
   * glass-to-glass, so the badge labels either one `EST`; `VideoLatencyEstimate`
   * carries which instrument produced it.
   */
  onLatencyEstimate?: (estimate: VideoLatencyEstimate | null) => void;
}

/**
 * Live video with the lowest latency the environment allows (#296).
 *
 * Tries WebRTC (WHEP) first — sub-second glass-to-glass against MediaMTX —
 * and falls back to [HlsVideo] (~2-4 s, LL-HLS) when WHEP is unavailable:
 * `webrtc: no` on the server, nginx route absent, ICE blocked by a network,
 * or no track arriving in time. The fallback is per-mount and NO LONGER
 * one-way: while HLS paints, a probe re-offers WHEP every [WHEP_RETRY_MS] and
 * the mount upgrades in place the moment one answers 201. A network where
 * WebRTC can never work therefore pays one small failed POST every 8 s per
 * mounted player - accepted deliberately, because the alternative was an
 * operator stuck on 2-3 s video for a whole sortie after one race at page
 * load ("the video should always work").
 *
 * Safe to ship AHEAD of the server config: while WHEP is unreachable the POST
 * fails immediately and every player lands on HLS exactly as before. That is
 * what happened for months — `mediamtx.yml` said `webrtc: yes` while the
 * running container published neither the signalling nor the ICE port, so this
 * branch was never once reached on prod and nobody could tell, because the
 * fallback is silent by design.
 *
 * WHEP went live on prod 2026-08-31: MediaMTX recreated with :8889 and :8189,
 * an nginx `/webrtc/` location, and 8189 already open on TCP and UDP. Verified
 * end to end — a WHEP POST answers 201 with ICE candidates on the public
 * address. The HLS fallback below is now a fallback rather than the only path.
 * See `docs/PLANS/video-latency-camera-control/IMPLEMENTATION.md`.
 */
export function LiveVideo({
  url,
  label,
  className,
  controls = true,
  onStreamState,
  onLatencyEstimate,
}: LiveVideoProps) {
  const whepUrl = whepUrlFromStreamUrl(url);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mode, setMode] = useState<'whep' | 'hls'>(whepUrl ? 'whep' : 'hls');
  // Whether the WebRTC branch has a decoded frame yet. HLS carries the same
  // fact in its own state; this branch had nowhere to put it, which is why it
  // rendered an unlabelled black rectangle while it negotiated - the one thing
  // an operator cannot tell apart from a dead camera.
  const [whepPainted, setWhepPainted] = useState(false);

  // Through refs so the WHEP effect keeps depending on the connection's own
  // inputs alone: a caller passing an inline callback must not tear down and
  // re-negotiate a live peer connection on every render of its own. Same
  // reasoning as the equivalent refs in HlsVideo — and both are read from an
  // event handler or an interval, never during render, which is where a ref is
  // the right instrument rather than a lint workaround.
  const onLatencyRef = useRef(onLatencyEstimate);
  onLatencyRef.current = onLatencyEstimate;
  const onStreamStateRef = useRef(onStreamState);
  onStreamStateRef.current = onStreamState;

  useEffect(() => {
    if (mode !== 'whep' || !whepUrl) return;
    const el = videoRef.current;
    if (!el) return;

    let cancelled = false;
    let painted = false;
    setWhepPainted(false);
    const pc = new RTCPeerConnection();
    // Nothing here can be allowed to throw synchronously into React, and every
    // failure has the same meaning: use HLS.
    const fallback = () => {
      if (!cancelled) setMode('hls');
    };
    // A negotiation that ANSWERED and then never produced a picture is a verdict
    // about this network, and enough of them stop the probe below from
    // re-offering a mount that cannot deliver. Both silent exits route through
    // here - the deadline, and a connection the browser abandons before the
    // deadline - because a strike counted in only one of them leaves the other
    // free to flap for ever: HLS unmounted and remounted every WHEP_RETRY_MS,
    // taking away a picture the operator already had.
    let gaveUp = false;
    const giveUp = () => {
      if (cancelled || painted || gaveUp) return;
      // Once per negotiation. The media deadline and `failed` are correlated -
      // six seconds of silence is overwhelmingly the case where ICE is about to
      // report failure too - and React commits the fallback a macrotask later,
      // so both can enter here before `cancelled` is set. Counting twice would
      // spend the whole ceiling on ONE bad negotiation and take away the retry
      // MAX_MEDIALESS_WHEP_ATTEMPTS exists to buy.
      gaveUp = true;
      mediaLessStrikes.set(whepUrl, {
        count: (mediaLessStrikes.get(whepUrl)?.count ?? 0) + 1,
        at: Date.now(),
      });
      fallback();
    };

    // Armed only once the answer is applied - see WHEP_MEDIA_DEADLINE_MS. Until
    // then the abort below is what bounds the wait.
    let mediaDeadline: number | null = null;
    // Armed when a painted picture stops arriving - see WHEP_MUTE_GRACE_MS.
    let muteGrace: number | null = null;
    // The gathering cap, held so teardown can clear it like every other timer
    // here rather than leaving one to fire into a closed connection.
    let gatherCap: number | null = null;
    const armMediaDeadline = () => {
      if (cancelled || mediaDeadline !== null) return;
      mediaDeadline = window.setTimeout(giveUp, WHEP_MEDIA_DEADLINE_MS);
    };

    // The previous tick's counters. `getStats()` reports cumulative totals, so
    // a CURRENT delay needs two samples to difference — see estimateWebrtcLatency.
    let previousSample: WebrtcStatsSample | null = null;
    // Started only once a frame is on the glass: polling a connection that
    // never negotiated would report null every second, which the badge would
    // render as NOT MEASURED over a picture HLS is about to take over anyway.
    let sampler: number | null = null;
    const startSampling = () => {
      if (sampler !== null) return;
      sampler = window.setInterval(() => {
        const report = onLatencyRef.current;
        if (!report || cancelled) return;
        void pc
          .getStats()
          .then((stats) => {
            if (cancelled) return;
            const sample = webrtcStatsSample(stats.values());
            report(estimateWebrtcLatency(sample, previousSample));
            previousSample = sample;
          })
          // A connection torn down between the interval firing and the promise
          // settling rejects here. One missed sample, not a broken player.
          .catch(() => undefined);
      }, WHEP_LATENCY_SAMPLE_MS);
    };

    // THE ONLY PROOF OF A PICTURE.
    //
    // Not `ontrack`: that fires when the ANSWER is applied, before a single RTP
    // packet exists, and clearing the deadline there disarmed the fallback at
    // signalling time - so a session whose ICE never connected kept a black tile
    // with nothing left to rescue it. On prod 2026-09-02, 5 of ~18 WHEP sessions
    // against a live mount closed `deadline exceeded while waiting connection`
    // while the operator watched black under a green FEED dot.
    //
    // Not the track's `unmute` either, which is the obvious replacement and is
    // still one step short: it fires on the first PACKET, not the first decoded
    // frame. This platform has already shipped an operator the difference -
    // 2026-08-31, MediaMTX forwarding H.264 High while advertising Baseline:
    // frames arrived, the decoder could not use them, the tile was black and the
    // connection was perfectly `connected` (STATUS.md). nginx now rewrites every
    // aircraft mount to its transcoded /cb twin, but that block has been lost to
    // hand-edits twice, and a player that cannot tell "packets" from "picture"
    // has no way to notice the third time.
    //
    // `loadeddata` is the element saying it has decoded data to show. It costs
    // nothing when it fires, and when a browser withholds it the player simply
    // lands on HLS - a picture 2-4 s late, never a black tile.
    const onFirstFrame = () => {
      if (cancelled || painted) return;
      painted = true;
      setWhepPainted(true);
      if (mediaDeadline !== null) window.clearTimeout(mediaDeadline);
      // This mount can carry a decodable picture on this network after all.
      mediaLessStrikes.delete(whepUrl);
      startSampling();
      // A picture is on the glass. Reported for the same reason HlsVideo reports
      // it: a surface that suppressed itself on this stream's earlier failure
      // has to learn that the failure is over.
      onStreamStateRef.current?.('playing');
    };
    el.addEventListener('loadeddata', onFirstFrame, { once: true });
    // Two corroborating signals, because this ONE gate now decides whether the
    // sub-second transport lives: an element already holding data when the
    // listener goes on would never fire `loadeddata` again, and
    // `requestVideoFrameCallback` - Chrome, Edge and Safari 15.4+ - is the more
    // precise "a frame was presented". Whichever arrives first wins; a browser
    // with neither still lands on HLS rather than a black tile.
    if (el.readyState >= 2) onFirstFrame();
    const rvfc = (el as HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: () => void) => number;
      cancelVideoFrameCallback?: (handle: number) => void;
    }).requestVideoFrameCallback?.call(el, onFirstFrame);

    // A connection that dies AFTER the picture arrives. Until this existed the
    // only routes to `fallback` were the media deadline and the signalling
    // catch, so once a frame had been painted the branch could never fail: the
    // RTMP publisher could drop or ICE could break on a network change and the
    // <video> would hold its last decoded frame indefinitely — no "Stream
    // offline", no fallback, no error reported. A still photograph under a live
    // aircraft's callsign is the one thing this whole surface exists to prevent.
    //
    // `disconnected` is deliberately NOT treated as fatal: ICE recovers from it
    // routinely on a roaming tablet, and dropping to HLS on every brief blip
    // would cost the sub-second path the change exists to deliver.
    pc.onconnectionstatechange = () => {
      if (cancelled) return;
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        onStreamStateRef.current?.('error');
        // Before a picture this is a media-less negotiation like any other and
        // must be counted; after one it is just a fallback.
        giveUp();
        fallback();
      }
    };

    pc.addTransceiver('video', { direction: 'recvonly' });
    pc.addTransceiver('audio', { direction: 'recvonly' });
    pc.ontrack = (ev) => {
      if (cancelled) return;
      // Video only, BEFORE the element is touched. The transcode publishes no
      // audio (`-an`), so an audio track should never arrive - but if one did,
      // replacing srcObject with an audio-only stream would stop `loadeddata`
      // for ever and spend a strike on a video that was working.
      if (ev.track.kind !== 'video') return;
      el.srcObject = ev.streams[0] ?? new MediaStream([ev.track]);
      // Muted autoplay is permitted without a gesture; the rejection some
      // browsers still raise is recoverable via the controls.
      void el.play().catch(() => undefined);

      // Liveness AFTER the picture. `mute` is the track saying media stopped
      // arriving - the publisher dropped, or the link broke - and it is the
      // only signal that arrives while the connection stays `connected` and the
      // element keeps showing its last frame. Debounced, because a blip is not
      // a drop; see WHEP_MUTE_GRACE_MS. Audio is ignored: the mount publishes
      // none, so its track is muted for ever by design.
      ev.track.onmute = () => {
        if (cancelled || muteGrace !== null) return;
        // Immediately, not after the grace: every consumer treats exactly
        // `playing` as "there is a picture to drag on", so reporting `loading`
        // here disarms the gimbal gesture surface over a frame that has stopped
        // moving without yet giving up the sub-second transport.
        onStreamStateRef.current?.('loading');
        muteGrace = window.setTimeout(() => {
          if (cancelled) return;
          onStreamStateRef.current?.('error');
          // HLS owns a reconnect budget built for exactly this, and the probe
          // carries the mount back up to sub-second when media returns. Before
          // a picture this is a media-less negotiation like any other and has
          // to be counted, or a mount that starts and stops before it ever
          // decodes flaps past the ceiling for ever.
          if (painted) fallback();
          else giveUp();
        }, WHEP_MUTE_GRACE_MS);
      };
      ev.track.onunmute = () => {
        if (muteGrace === null) return;
        window.clearTimeout(muteGrace);
        muteGrace = null;
      };
    };

    // Negotiating is not playing, and the gimbal gates read this: `pictureLive`
    // is `state === null || state === 'playing'`, so leaving the report at its
    // default would arm a drag surface over a tile with no picture on it.
    onStreamStateRef.current?.('loading');

    const negotiation = new AbortController();
    const answerTimeout = window.setTimeout(
      () => negotiation.abort(),
      WHEP_ANSWER_TIMEOUT_MS,
    );

    // We do not trickle, so the offer has to carry the candidates itself -
    // see WHEP_ICE_GATHER_MS. Resolves on `complete` or on the cap, whichever
    // comes first, and never rejects: a partial candidate set is still an offer.
    const gatheringSettled = () =>
      new Promise<void>((resolve) => {
        if (pc.iceGatheringState === 'complete') {
          resolve();
          return;
        }
        const finish = () => {
          if (gatherCap !== null) window.clearTimeout(gatherCap);
          gatherCap = null;
          pc.removeEventListener('icegatheringstatechange', onChange);
          resolve();
        };
        const onChange = () => {
          if (pc.iceGatheringState === 'complete') finish();
        };
        gatherCap = window.setTimeout(finish, WHEP_ICE_GATHER_MS);
        pc.addEventListener('icegatheringstatechange', onChange);
      });

    void (async () => {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await gatheringSettled();
        if (cancelled) return;
        const res = await fetch(whepUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/sdp' },
          // `pc.localDescription`, NOT the `offer` returned by createOffer:
          // that one is a snapshot from before gathering and contains no
          // candidates at all, which is how every offer we have ever sent went
          // out empty.
          body: pc.localDescription?.sdp ?? offer.sdp,
          signal: negotiation.signal,
        });
        if (!res.ok) throw new Error(`WHEP ${res.status}`);
        const answer = await res.text();
        if (cancelled) return;
        await pc.setRemoteDescription({ type: 'answer', sdp: answer });
        window.clearTimeout(answerTimeout);
        armMediaDeadline();
      } catch {
        window.clearTimeout(answerTimeout);
        // NOT a strike: "nobody answered" is exactly what the probe below exists
        // to keep asking about - an aircraft that has not started publishing yet
        // must not be able to latch this player onto HLS for the sortie.
        fallback();
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(answerTimeout);
      if (mediaDeadline !== null) window.clearTimeout(mediaDeadline);
      if (muteGrace !== null) window.clearTimeout(muteGrace);
      if (gatherCap !== null) window.clearTimeout(gatherCap);
      if (sampler !== null) window.clearInterval(sampler);
      el.removeEventListener('loadeddata', onFirstFrame);
      if (rvfc !== undefined) {
        (el as HTMLVideoElement & { cancelVideoFrameCallback?: (h: number) => void })
          .cancelVideoFrameCallback?.call(el, rvfc);
      }
      pc.close();
      if (el.srcObject) el.srcObject = null;
    };
  }, [mode, whepUrl]);

  // THE WAY BACK UP. Falling back must not be a life sentence: "one-way and
  // per-mount" was the right shape while WHEP was permanently broken (nginx
  // route gone, undecodable High profile) - retrying a thing that cannot work
  // is noise. Both of those are fixed, which inverts the failure that matters:
  // a COP opened before the aircraft starts publishing, or during one of its
  // link drops, tried WHEP once, got "no one is publishing", and sat on 2-3 s
  // HLS for the rest of the sortie while sub-second sat available one retry
  // away. The operator's words: "the video should always work."
  //
  // So while HLS is showing the picture, quietly re-offer WHEP on an interval.
  // A probe is a real offer from a throwaway connection - the only response
  // that proves BOTH "routed" and "someone is publishing" is a 201 - and on
  // success the mode flips back and the real effect above negotiates afresh.
  // The probe's own half-open session is abandoned deliberately: its ICE never
  // starts, and the server reaps it in seconds, which is cheaper than a
  // DELETE round-trip that can itself fail. HLS keeps painting throughout; the
  // switch only happens once WHEP has proven it can answer, so the worst case
  // of a probe is nothing, never a black tile.
  //
  // This also closes the loop with the mid-stream path: a WebRTC connection
  // that dies falls back to HLS above, and this effect then carries it back to
  // sub-second when the publisher returns - the two transports become a
  // self-healing pair instead of a one-way ratchet.
  useEffect(() => {
    if (mode !== 'hls' || !whepUrl) return;
    let cancelled = false;
    let inFlight = false;
    const probe = window.setInterval(() => {
      if (inFlight || cancelled) return;
      // ...but a mount that keeps answering and keeps delivering nothing has
      // already told us what a probe would ask, so it serves its backoff first:
      // re-offering on every tick would blank the picture for
      // WHEP_MEDIA_DEADLINE_MS each time.
      //
      // The interval keeps TICKING rather than the effect returning early. The
      // wait has to be able to end without anything re-mounting this component,
      // and an effect that returned early could only be re-run by a remount -
      // which is exactly the "reload the tab to get sub-second video back" this
      // fixes.
      if (!whepProbeAllowed(whepUrl, Date.now())) return;
      inFlight = true;
      const pc = new RTCPeerConnection();
      pc.addTransceiver('video', { direction: 'recvonly' });
      // A probe that hangs must not take the way back up with it: `inFlight`
      // would stay true, every later tick would short-circuit on it, and the
      // mount would sit on HLS for the rest of the sortie with nothing left
      // asking. Same bound as the negotiation's own POST.
      const abort = new AbortController();
      const timeout = window.setTimeout(() => abort.abort(), WHEP_ANSWER_TIMEOUT_MS);
      void (async () => {
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          const res = await fetch(whepUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/sdp' },
            body: offer.sdp,
            signal: abort.signal,
          });
          if (res.ok && !cancelled) setMode('whep');
        } catch {
          // Unreachable is just "not yet" - the next tick asks again.
        } finally {
          window.clearTimeout(timeout);
          pc.close();
          inFlight = false;
        }
      })();
    }, WHEP_RETRY_MS);
    return () => {
      cancelled = true;
      window.clearInterval(probe);
    };
  }, [mode, whepUrl]);

  if (mode === 'hls') {
    return (
      <HlsVideo
        url={url}
        label={label}
        className={className}
        controls={controls}
        onStreamState={onStreamState}
        onLatencyEstimate={onLatencyEstimate}
      />
    );
  }
  return (
    <div className="relative h-full w-full">
      <video
        ref={videoRef}
        className={className}
        muted
        playsInline
        autoPlay
        controls={controls}
        aria-label={`${label} (low latency)`}
      />
      {!whepPainted && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/70 pointer-events-none"
          role="status"
        >
          <span className="text-[10px] uppercase tracking-[0.25em] text-on-media-muted-fg">
            Connecting…
          </span>
        </div>
      )}
    </div>
  );
}
